import { Menu, MenuItem, makeStyles } from '@material-ui/core';
import React, { useCallback, useMemo, useState } from 'react';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import NumberFormat from 'react-number-format';
import PropTypes from 'prop-types';
import InfoIcon from '@material-ui/icons/Info';
import Label from '../../../../Label';
import AgentCommission from '../../../AgentCommission';
import getCurrencySymbol from '../../../../../helpers/currencySymbol';
import { FlightFareSources, SearchStatus } from '../../../../../constants';
// import ClassDetailsModal from '../../ClassDetailsModal';
import getChangePolicyLabel from '../../../../../helpers/getChangePolicyLabel';
import checkFlightItineraryPrivateFare from '../../../../../helpers/checkFlightItineraryPrivateFare';
import { useDispatch, useSelector } from 'react-redux';
import { checkIsLoadedIn } from '../../../../../store/slices/user';
import { getUpsellFaresRequest } from '../../../../../store/slices/flight';
import formatFlightFareKey from '../../../../../helpers/formatters/formatFlightFareKey';
import getBaggageInfo from '../getBaggageInfo';

const MenuStyles = makeStyles((theme) => ({
  selected: {
    color: theme.palette.common.white,
    backgroundColor: `${theme.palette.primary.main}!important`,
  },
  root: {
    fontFamily: 'var(--fontFamilyBody)',
    display: 'block',
  },
}));

const FlightFareClasses = ({ flightItinerary, handleAddToTripClick, isTripSelected }) => {
  const classes = MenuStyles();

  const [anchorEl, setAnchorEl] = useState(null);
  // const [classDetailsModalFare, setClassDetailsModalFare] = useState(null);

  const [expandedItems, setExpandedItems] = useState({});
  const { isLoggedIn } = useSelector(checkIsLoadedIn);
  const dispatch = useDispatch();

  // "Show all fares" (Travelport only): the search response caps upsells at ~4; the backend can
  // fetch the airline's complete branded-fare list for this exact itinerary.
  const upsellEntry = useSelector((store) => store.flight.upsellFares[flightItinerary.flightKey]);
  const upsellStatus = upsellEntry && upsellEntry.status;

  const canFetchAllFares =
    !isTripSelected &&
    Boolean(flightItinerary.isUpsellFaresEnabled) &&
    Boolean(flightItinerary.flightKey) &&
    upsellStatus !== SearchStatus.DONE;

  const handleFetchAllFares = (event) => {
    event.stopPropagation();
    if (upsellStatus === SearchStatus.SEARCHING) return;
    dispatch(getUpsellFaresRequest({ flightKey: flightItinerary.flightKey, flightItinerary }));
  };

  const handleToggleExpand = (event, index) => {
    setExpandedItems((prevExpandedItems) => ({
      ...prevExpandedItems,
      [index]: !prevExpandedItems[index],
    }));
    event.stopPropagation();
  };
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const setSelectedFare = (event, fareIndex) => {
    if (handleAddToTripClick) {
      handleAddToTripClick(event, fareIndex);
      handleClose();
    }
  };

  // const handleShowClassDetails = (event, fare, fareIndex) => {
  //   event.stopPropagation();
  //   setClassDetailsModalFare({ fare, fareIndex });
  // };

  const flightsList = useMemo(
    () => flightItinerary.flightSegments.map((segment) => segment.flights).flat(),
    [flightItinerary.flightSegments],
  );

  const getGroupedFlights = useCallback(
    (index) => {
      const groupedFares = flightsList.reduce((acc, flight) => {
        const currentFare = flight.allFares[index];
        if (
          currentFare &&
          currentFare.priceClassDescriptions &&
          currentFare.priceClassDescriptions.length > 0
        ) {
          const fareKey = `${currentFare.fareBrandName}-${currentFare.priceClassDescriptions.join('-')}`;
          if (!acc[fareKey]) {
            acc[fareKey] = {
              fareBrandName: currentFare.fareBrandName,
              priceClassDescriptions: currentFare.priceClassDescriptions,
              flights: [],
            };
          }
          acc[fareKey].flights.push(flight);
        }
        return acc;
      }, {});
      return groupedFares;
    },
    [flightsList],
  );

  // const handleCloseClassDetails = () => {
  //   setClassDetailsModalFare(null);
  // };
  const viewFareDetails = useCallback(
    (fare) => (
      <ul className="mt-2 text-sm list-disc pl-5 text-wrap">
        {fare.priceClassDescriptions.map((description) => (
          <li className="whitespace-normal break-words text-wrap text-left" key={description}>
            {description}
          </li>
        ))}
      </ul>
    ),
    [],
  );

  const currencySymbol = getCurrencySymbol((flightItinerary.cost || {}).currencyCode);
  const ChevronIcon = anchorEl ? FaChevronUp : FaChevronDown;
  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        className="inline-flex items-center gap-1 text-center"
      >
        More Classes
        <ChevronIcon className="inline-block ml-1" />
      </button>
      <Menu
        className="modal-safe-click-zone"
        PaperProps={{
          style: {
            border: '1px solid #d3d4d5',
            width: 'min(520px, calc(100vw - 32px))',
            maxHeight: 'calc(70% - 96px)',
          },
        }}
        getContentAnchorEl={null}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        id="customized-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        {flightItinerary.flightSegments[0].flights[0].allFares.map((fare, index) => {
          const selectedFareKey = formatFlightFareKey(
            flightItinerary.flightSegments[0].flights[0].selectedFare,
          );
          const isSelected = selectedFareKey === formatFlightFareKey(fare);

          // Per-leg baggage for this fare row, reusing the same builder as the result card.
          // `fareIndex` makes it read allFares[index] across every segment (legs can differ,
          // e.g. Economy out + Premium Economy in) instead of the selectedFare.
          const baggageItems = getBaggageInfo(flightItinerary, {
            fareIndex: index,
            className: isSelected ? 'text-white' : undefined,
            showInfoIndicator: 'icon-only',
          }).items;
          const changePolicyLabel = getChangePolicyLabel(fare, currencySymbol);

          return (
            <MenuItem
              component="li"
              key={`${index.toString()}`}
              selected={isSelected}
              classes={classes}
              onClick={(event) => {
                setSelectedFare(event, index);
              }}
              className="py-3 px-4 block"
              style={
                index < flightItinerary.flightSegments[0].flights[0].allFares.length - 1
                  ? { borderBottom: '1px solid #a1a5ab' }
                  : undefined
              }
            >
              <div className="flex gap-3">
                {/* LEFT column - has the class name + fare info bullets */}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-normal whitespace-normal">
                    {flightItinerary.alternateFareCabinClassNames &&
                      flightItinerary.alternateFareCabinClassNames[index]}
                  </div>
                  <div className="text-xs flex flex-wrap items-center mt-1 gap-1">
                    <Label
                      text={
                        <>
                          {fare.pricingSubsource &&
                          fare.pricingSubsource === FlightFareSources.NDC ? (
                            <>
                              <span className="capitalize">{`${fare.refundabilityLabel},`}</span>
                              <b> NDC Fare</b>
                            </>
                          ) : (
                            <span className="capitalize">{fare.refundabilityLabel}</span>
                          )}
                          {fare.fareDescription && `, ${fare.fareDescription}`}
                        </>
                      }
                      level={fare.isRefundable ? 'success' : 'danger'}
                      inline
                      tooltipPlacement="top"
                      descriptionOnHover={fare.refundPolicyText}
                      showInfoIndicator="icon-only"
                    />
                    {changePolicyLabel != null && (
                      <span className="inline">
                        <span className="mr-1">·</span>
                        <Label
                          text={changePolicyLabel}
                          level=""
                          inline
                          tooltipPlacement="top"
                          descriptionOnHover={fare.changePolicyText}
                          showInfoIndicator="icon-only"
                        />
                      </span>
                    )}
                    {/* Inline baggage — same labels/tooltips as the result card, each preceded
                        by the "·" separator to match the surrounding fare-info row */}
                    {baggageItems.map((item) => (
                      <span className="inline" key={item.key}>
                        <span className="mr-1">·</span>
                        {item}
                      </span>
                    ))}
                  </div>
                  {fare.agentCommission > 0 && (
                    <div className="mt-1">
                      <AgentCommission
                        mode={flightItinerary.mode}
                        agentCommission={fare.agentCommission}
                        currencyCode={flightItinerary.cost && flightItinerary.cost.currencyCode}
                        className="py-1 inline-flex text-sm"
                      />
                    </div>
                  )}
                </div>
                {/* RIGHT column - has the price + private fare icon */}
                <div className="text-right text-sm font-normal whitespace-nowrap flex-shrink-0 w-24">
                  <NumberFormat
                    value={fare.price}
                    displayType="text"
                    thousandSeparator
                    prefix={currencySymbol}
                    fixedDecimalScale="true"
                    decimalScale="2"
                  />
                  {isLoggedIn &&
                    (fare.fareAcccountCode || checkFlightItineraryPrivateFare(undefined, fare)) && (
                      <div className="flex justify-end">
                        <Label
                          icon={<InfoIcon style={{ fontSize: '20px' }} />}
                          level="success"
                          italic
                          inline
                          descriptionOnHover={
                            fare.fareAcccountCode
                              ? `${fare.fareAcccountCode} rate code match with your selection.`
                              : 'Private/Negotiated fare'
                          }
                        />
                      </div>
                    )}
                </div>
              </div>
              {fare.priceClassDescriptions && fare.priceClassDescriptions.length > 0 && (
                <div className="mt-2">
                  <button
                    type="button"
                    // onClick={(event) => handleShowClassDetails(event, fare, index)}
                    onClick={(event) => handleToggleExpand(event, index)}
                    className="underline cursor-pointer inline-flex items-center gap-1 text-sm font-normal"
                  >
                    Class details
                    <FaChevronDown className="h-3" />
                  </button>
                </div>
              )}
              <div className="w-full">
                {flightsList.length === 1 ? (
                  <div className="w-full">
                    {expandedItems[index] && fare.priceClassDescriptions && viewFareDetails(fare)}
                  </div>
                ) : (
                  Object.values(getGroupedFlights(index)).map((currentFare, fareIndex) => (
                    <div key={`${currentFare.fareBrandName}-${fareIndex.toString()}`}>
                      {expandedItems[index] && currentFare.priceClassDescriptions && (
                        <>
                          <p className="p-1 mt-1 text-xs leading-3 font-semibold whitespace-normal break-words text-wrap">
                            {currentFare.fareBrandName} (
                            {currentFare.flights
                              .map(
                                (flight) =>
                                  `${flight.originAirportCode} - ${flight.destinationAirportCode}`,
                              )
                              .join(', ')}
                            )
                          </p>
                          {viewFareDetails(currentFare)}
                        </>
                      )}
                    </div>
                  ))
                )}
              </div>
            </MenuItem>
          );
        })}
        {canFetchAllFares && (
          <li className="py-2 px-4 text-center" style={{ borderTop: '1px solid #a1a5ab' }}>
            <button
              type="button"
              onClick={handleFetchAllFares}
              disabled={upsellStatus === SearchStatus.SEARCHING}
              className="underline cursor-pointer text-sm font-normal disabled:opacity-50 disabled:cursor-wait"
            >
              {upsellStatus === SearchStatus.SEARCHING ? 'Searching…' : 'Search additional fares'}
            </button>
            {upsellStatus === SearchStatus.ERROR && (
              <div className="text-xs text-red-600 mt-1">
                Couldn&apos;t load additional fares — click to retry
              </div>
            )}
          </li>
        )}
      </Menu>

      {/* {classDetailsModalFare && (
        <ClassDetailsModal
          priceClassDescriptions={classDetailsModalFare.fare.priceClassDescriptions}
          groupedFlights={
            flightsList.length > 1 ? getGroupedFlights(classDetailsModalFare.fareIndex) : undefined
          }
          onClose={handleCloseClassDetails}
        />
      )} */}
    </>
  );
};

FlightFareClasses.propTypes = {
  flightItinerary: PropTypes.shape().isRequired,
  handleAddToTripClick: PropTypes.func.isRequired,
  isTripSelected: PropTypes.bool,
};

FlightFareClasses.defaultProps = {
  isTripSelected: false,
};

export default React.memo(FlightFareClasses);
